import { memo, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { DropzoneProps, DropzoneRef } from 'react-dropzone';
import classNames from 'classnames/bind';
import styles from './BaseUploadFileV2.module.scss';

export type BaseUploadFileProps = DropzoneProps &
  React.RefAttributes<DropzoneRef> & {
    onChange: (data: any) => void;
    url?: string;
    errorMessage?: string;
    isViewMode?: boolean;
    multiple?: boolean;
    height?: number | string;
  };

type FormUploadFileRef = {
  opendUploadFile: () => void;
};

type SelectedFile = {
  file: File;
  fileName: string;
  fileSize: string;
  fileType: string;
  fileUrl: string;
};

const cx = classNames.bind(styles);
const BaseUploadFileV2 = forwardRef<FormUploadFileRef, BaseUploadFileProps>((props, ref) => {
  const { onChange, errorMessage, url, isViewMode, multiple = false, height } = props;
  const boxHeight = height ?? 171;

  useImperativeHandle(ref, () => ({
    opendUploadFile() {
      openFileDialog();
    },
  }));

  const allowedTypes = {
    'image/png': 'png',
    'image/webp': 'webp',
    'image/jpeg': 'jpeg',
  };

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [imgSrc, setImgSrc] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  const formatFileSize = (size: number) => {
    if (size >= 1024 ** 3) return `${(size / 1024 ** 3).toFixed(2)} GB`;
    if (size >= 1024 ** 2) return `${(size / 1024 ** 2).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${size} B`;
  };

  const toSelectedFile = (file: File): SelectedFile | null => {
    const ext = allowedTypes[file.type as keyof typeof allowedTypes];
    if (!ext) return null;
    return {
      file,
      fileName: file.name,
      fileSize: formatFileSize(file.size),
      fileType: ext,
      fileUrl: URL.createObjectURL(file),
    };
  };

  const emitChange = (files: SelectedFile[]) => {
    if (multiple) {
      onChange({
        files: files.map((f) => f.file),
        file: files[0]?.file ?? null,
        fileName: files.map((f) => f.fileName).join(', '),
        fileSize: files.map((f) => f.fileSize).join(', '),
        fileType: files[0]?.fileType ?? '',
        fileUrl: files[0]?.fileUrl ?? '',
        previews: files.map((f) => f.fileUrl),
      });
      return;
    }

    const first = files[0];
    if (!first) {
      onChange({ file: null, fileName: '', fileSize: '', fileType: '', fileUrl: '' });
      return;
    }
    onChange({
      file: first.file,
      fileName: first.fileName,
      fileSize: first.fileSize,
      fileType: first.fileType,
      fileUrl: first.fileUrl,
    });
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleUploadFiles = (fileList: FileList | File[] | undefined) => {
    if (!fileList || fileList.length === 0) return;

    const incoming = Array.from(fileList);
    const valid: SelectedFile[] = [];
    let hasInvalid = false;
    let hasOversized = false;

    for (const file of incoming) {
      if (file.size > MAX_FILE_SIZE) {
        hasOversized = true;
        continue;
      }
      const selected = toSelectedFile(file);
      if (!selected) {
        hasInvalid = true;
        continue;
      }
      valid.push(selected);
    }

    if (valid.length === 0) {
      if (hasOversized) {
        setError('File size must be less than 10MB');
      } else {
        setError('Unsupported file type. Allowed file types are PNG, JPG, JPEG, and WEBP.');
      }
      return;
    }

    if (hasOversized) {
      setError('Some files were skipped. Maximum file size: 10MB.');
    } else if (hasInvalid) {
      setError('Some files were skipped. Allowed types: PNG, JPG, JPEG, WEBP.');
    } else {
      setError('');
    }

    const next = multiple ? [...selectedFiles, ...valid] : [valid[0]];
    setSelectedFiles(next);
    setImgSrc(next[0]?.fileUrl ?? '');
    emitChange(next);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUploadFiles(e.target.files ?? undefined);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    handleUploadFiles(e.dataTransfer.files);
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  const removeFileAt = (index: number) => {
    const removed = selectedFiles[index];
    if (removed) URL.revokeObjectURL(removed.fileUrl);
    const next = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(next);
    setImgSrc(next[0]?.fileUrl ?? '');
    emitChange(next);
  };

  useEffect(() => {
    if (!multiple) {
      setImgSrc(url ? url : '');
    }
  }, [url, multiple]);

  const showEmptyUpload = !isViewMode && selectedFiles.length === 0 && !imgSrc;
  const showSinglePreview = !isViewMode && !multiple && (!!imgSrc || selectedFiles.length === 1);
  const showMultiPreview = !isViewMode && multiple && selectedFiles.length > 0;

  return (
    <div className="">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isViewMode ? (
          <div className={cx('preview')} style={{ height: boxHeight }}>
            <div className={cx('image-wrapper')}>
              {imgSrc && <img src={imgSrc} alt="" />}
            </div>
          </div>
        ) : showMultiPreview ? (
          <div className={cx('multi-preview')}>
            <div className={cx('multi-preview-grid')}>
              {selectedFiles.map((item, index) => (
                <div key={`${item.fileName}-${index}`} className={cx('multi-preview-item')}>
                  <img src={item.fileUrl} alt={item.fileName} />
                  <button
                    type="button"
                    className={cx('multi-preview-remove')}
                    onClick={() => removeFileAt(index)}
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className={cx('btn-add-more')} onClick={openFileDialog}>
              Add more photos
            </button>
          </div>
        ) : showSinglePreview ? (
          <div className={cx('preview')} style={{ height: boxHeight }}>
            <div className={cx('image-wrapper')}>
              {imgSrc && <img src={imgSrc} alt="" />}
            </div>
            <div className={cx('divider')} style={{ height: typeof boxHeight === 'number' ? boxHeight - 4 : undefined }}></div>
            <div className={cx('btn-remove')} onClick={openFileDialog}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.75 18.75H16.75C17.8546 18.75 18.75 17.8546 18.75 16.75V2.75C18.75 1.64543 17.8546 0.75 16.75 0.75L2.75 0.75C1.64543 0.75 0.75 1.64543 0.75 2.75L0.75 16.75C0.75 17.8546 1.64543 18.75 2.75 18.75ZM2.75 18.75L13.75 7.75L18.75 12.75M7.75 6.25C7.75 7.07843 7.07843 7.75 6.25 7.75C5.42157 7.75 4.75 7.07843 4.75 6.25C4.75 5.42157 5.42157 4.75 6.25 4.75C7.07843 4.75 7.75 5.42157 7.75 6.25Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p>Change</p>
            </div>
          </div>
        ) : showEmptyUpload ? (
          <div className={cx({
            'upload-section': true,
            error: Boolean(errorMessage) || error !== '',
          }, {
            'error-upload': error !== '' || Boolean(errorMessage),
            'drag-active': isDragActive,
          })} style={{ height: boxHeight }}>
            <p className={cx('upload-text')}>
              Drag and drop your {multiple ? 'files' : 'file'} here or{' '}
              <span className={cx('click-upload')} onClick={openFileDialog}>Click to upload</span>
            </p>
            <p className={cx('upload-description')}>
              Maximum file size: 10MB{multiple ? ' · Multiple images supported' : ''}
            </p>
          </div>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept=".png,.webp,.jpg,.jpeg"
          style={{ display: 'none' }}
          id="file-upload"
          multiple={multiple}
          onChange={handleFileChange}
        />
      </div>
      {(error || errorMessage) && (
        <p className={cx('error-message')}>{error || errorMessage}</p>
      )}
    </div>
  );
});
export default memo(BaseUploadFileV2);
